import { useEffect, useState } from "react";
import { Globe, ImageIcon, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DesignImageUpload } from "@/components/experience/design-image-upload";
import { useProject } from "@/components/project-context";
import { useConnectToken } from "@/hooks/use-connect-token";
import { LimitReachedBanner } from "@/components/limit-reached-banner";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import type { WalkthroughMode } from "./panel-types";
import { ModeCard } from "./panel-run-mode-card";
import { RunLiveSection } from "./panel-run-live-section";
import { useRunDialogMutations } from "./use-run-dialog-mutations";

interface RunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioId: string;
  projectId: string;
}

export function RunDialog({
  open,
  onOpenChange,
  scenarioId,
  projectId,
}: RunDialogProps) {
  const project = useProject();
  const requests = useWorkRequests(projectId);
  const { data: projectData } = trpc.project.getById.useQuery(
    { id: projectId },
    { enabled: open },
  );

  const [selectedMode, setSelectedMode] = useState<WalkthroughMode>("live_url");
  const [designReviewStep, setDesignReviewStep] = useState<"setup" | "upload">(
    "setup",
  );
  const [pendingWalkthroughId, setPendingWalkthroughId] = useState<
    string | null
  >(null);
  const [targetUrl, setTargetUrl] = useState("");
  const [selectedRunnerId, setSelectedRunnerId] = useState("");
  const [showConnectCmd, setShowConnectCmd] = useState(false);
  const connectToken = useConnectToken(projectId);

  // Seed targetUrl with the project's productUrl when the dialog first opens.
  useEffect(() => {
    if (open && project.productUrl && !targetUrl) {
      setTargetUrl(project.productUrl);
    }
  }, [open, project.productUrl, targetUrl]);

  const { token: mintedToken, error: mintError, generate } = connectToken;
  useEffect(() => {
    if (showConnectCmd && !mintedToken && !mintError) generate();
  }, [showConnectCmd, mintedToken, mintError, generate]);

  const { data: runnerStatus } = trpc.walkthrough.runnerStatus.useQuery(
    {},
    {
      enabled: open && selectedMode === "live_url",
      refetchInterval: open && selectedMode === "live_url" ? 5000 : false,
    },
  );

  const { data: designImages } = trpc.walkthrough.listDesignImages.useQuery(
    { walkthroughId: pendingWalkthroughId ?? "" },
    { enabled: !!pendingWalkthroughId },
  );

  function close() {
    onOpenChange(false);
    setSelectedMode("live_url");
    setDesignReviewStep("setup");
    setPendingWalkthroughId(null);
    setTargetUrl("");
    setSelectedRunnerId("");
    setShowConnectCmd(false);
  }

  const {
    autoRunMutation,
    createDesignReviewMutation,
    executeDesignReviewMutation,
    isRunning,
  } = useRunDialogMutations({
    scenarioId,
    projectId,
    onAutoRunSuccess: (walkthroughId) => {
      close();
      window.open(`/projects/${projectId}/runs/${walkthroughId}`, "_blank");
    },
    onDesignReviewCreated: (walkthroughId) => {
      setPendingWalkthroughId(walkthroughId);
      setDesignReviewStep("upload");
    },
    onDesignReviewExecuted: () => {
      close();
    },
  });

  const connected = (runnerStatus?.runners?.length ?? 0) > 0;
  const isQueueRun =
    selectedMode === "live_url" &&
    !connected &&
    projectData?.requestExecutor !== "cloud";

  const handleRunClick = () => {
    if (selectedMode === "design_review") {
      createDesignReviewMutation.mutate({
        scenarioId,
      } as unknown as { scenarioId: string });
    } else if (selectedMode === "live_url") {
      if (!targetUrl) {
        toast.error("Please enter a target URL");
        return;
      }
      if (!connected && projectData?.requestExecutor !== "cloud") {
        requests.queue({ kind: "run", scenarioId, targetUrl });
        close();
        return;
      }
      autoRunMutation.mutate({
        scenarioId,
        targetUrl,
        runnerId: selectedRunnerId || undefined,
      } as unknown as {
        scenarioId: string;
        targetUrl: string;
        runnerId?: string;
      });
    }
  };

  const handleExecuteDesignReview = () => {
    if (!pendingWalkthroughId) return;
    executeDesignReviewMutation.mutate({ walkthroughId: pendingWalkthroughId });
  };

  const imageCount = designImages?.length ?? 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {designReviewStep === "upload"
              ? "Upload design images"
              : "Run walkthrough"}
          </DialogTitle>
          <DialogDescription>
            {designReviewStep === "upload"
              ? "Upload Figma exports, screenshots, or wireframes to review."
              : "The persona evaluates using your project’s domain knowledge."}
          </DialogDescription>
        </DialogHeader>

        <LimitReachedBanner resource="walkthroughs" />

        {designReviewStep === "upload" ? (
          <>
            <DesignImageUpload walkthroughId={pendingWalkthroughId!} />
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={handleExecuteDesignReview}
                disabled={
                  imageCount === 0 || executeDesignReviewMutation.isPending
                }
              >
                {executeDesignReviewMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Reviewing...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Run review ({imageCount} image
                    {imageCount !== 1 ? "s" : ""})
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>What should the persona evaluate?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <ModeCard
                    icon={Globe}
                    title="Live website"
                    description="Navigate a real site and evaluate it"
                    selected={selectedMode === "live_url"}
                    onClick={() => setSelectedMode("live_url")}
                  />
                  <ModeCard
                    icon={ImageIcon}
                    title="Design mockups"
                    description="Review uploaded Figma exports or screenshots"
                    selected={selectedMode === "design_review"}
                    onClick={() => setSelectedMode("design_review")}
                  />
                </div>
              </div>

              {selectedMode === "live_url" && (
                <RunLiveSection
                  targetUrl={targetUrl}
                  setTargetUrl={setTargetUrl}
                  runnerStatus={runnerStatus}
                  selectedRunnerId={selectedRunnerId}
                  setSelectedRunnerId={setSelectedRunnerId}
                  isQueueRun={isQueueRun}
                  showConnectCmd={showConnectCmd}
                  setShowConnectCmd={setShowConnectCmd}
                  connectToken={connectToken}
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                onClick={handleRunClick}
                disabled={
                  isRunning ||
                  requests.isPending ||
                  (selectedMode === "live_url" && !targetUrl)
                }
              >
                {isRunning || requests.isPending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    {selectedMode === "design_review"
                      ? "Creating..."
                      : isQueueRun
                        ? "Queueing..."
                        : "Starting..."}
                  </>
                ) : (
                  <>
                    {selectedMode === "design_review" ? (
                      <>
                        <ImageIcon className="mr-2 h-4 w-4" />
                        Next: upload images
                      </>
                    ) : isQueueRun ? (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Queue run
                      </>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Run
                      </>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
